import { useEffect, useMemo, useState } from "react";
import { BRANDS } from "@/lib/brands";
import { T } from "@/lib/theme";

/**
 * OrbitViewer — interactive SVG.
 * Default state : Erenown at centre, 5 brands on the outer ring.
 * Selected state: selected brand scales and moves to centre (on top of a faded Erenown),
 *                 its 4 modules appear on the inner ring, other brands fade back.
 *
 * Motion is restrained: single 1.2s cubic-bezier transition on transform + opacity.
 */
const SIZE = 720;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R_OUTER = 260;
const R_INNER = 150;

function brandPos(i, total = BRANDS.length) {
  const a = (i / total) * Math.PI * 2 - Math.PI / 2;
  return { x: CX + Math.cos(a) * R_OUTER, y: CY + Math.sin(a) * R_OUTER, a };
}

function modulePos(i, total) {
  const a = (i / total) * Math.PI * 2 - Math.PI / 4;
  return { x: CX + Math.cos(a) * R_INNER, y: CY + Math.sin(a) * R_INNER };
}

/**
 * splitLabel — break a multi-word label into two roughly equal lines so it
 * can be rendered as a small stacked block beside its module circle.
 * Falls back to a single line for short labels.
 */
function splitLabel(name) {
  const words = name.split(/\s+/);
  if (words.length <= 2) return [name];

  // Find the split point that minimises difference in line length.
  let bestIdx = Math.ceil(words.length / 2);
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(" ");
    const b = words.slice(i).join(" ");
    const diff = Math.abs(a.length - b.length);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  return [
    words.slice(0, bestIdx).join(" "),
    words.slice(bestIdx).join(" "),
  ];
}

export default function OrbitViewer({ initialBrand = null, onChange }) {
  const [selected, setSelected] = useState(initialBrand);
  useEffect(() => { setSelected(initialBrand); }, [initialBrand]);
  const selectedBrand = useMemo(() => BRANDS.find((b) => b.id === selected) || null, [selected]);

  const pick = (id) => {
    const next = id === selected ? null : id;
    setSelected(next);
    onChange && onChange(next);
  };

  return (
    <div className="grid grid-cols-12 gap-10 items-start" data-testid="orbit-viewer">
      {/* The orbit stage */}
      <div className="col-span-12 lg:col-span-7 relative">
        <div className="relative mx-auto" style={{ maxWidth: 720 }}>
          <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full h-auto">
            <defs>
              <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={T.accent} stopOpacity="0.5" />
                <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
              </radialGradient>
              <radialGradient id="warmGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={T.warm} stopOpacity="0.45" />
                <stop offset="100%" stopColor={T.warm} stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* ambient rings */}
            <g opacity="0.35">
              <circle cx={CX} cy={CY} r={R_OUTER + 40} fill="none" stroke={T.border} strokeDasharray="1 14" />
              <circle cx={CX} cy={CY} r={R_OUTER} fill="none" stroke={T.border} />
              <circle cx={CX} cy={CY} r={R_INNER} fill="none" stroke={T.border} strokeDasharray="2 6" />
              <circle cx={CX} cy={CY} r={60} fill="none" stroke={T.border} />
              <line x1="0" y1={CY} x2={SIZE} y2={CY} stroke={T.border} strokeOpacity="0.4" />
              <line x1={CX} y1="0" x2={CX} y2={SIZE} stroke={T.border} strokeOpacity="0.4" />
            </g>

            {/* slow rotating ambient ring */}
            <g className="spin-slower" style={{ transformOrigin: `${CX}px ${CY}px` }}>
              <circle cx={CX} cy={CY} r={R_OUTER + 80} fill="none" stroke={T.border} strokeDasharray="1 26" opacity="0.35" />
            </g>

            {/* Modules — only render when a brand is selected */}
            {selectedBrand && selectedBrand.modules.map((m, i) => {
              const total = selectedBrand.modules.length;
              const a = (i / total) * Math.PI * 2 - Math.PI / 4;
              const p = { x: CX + Math.cos(a) * R_INNER, y: CY + Math.sin(a) * R_INNER };

              // Label position — push outward so it never sits on top of the
              // circle. Anchor text away from the centre so the four labels
              // fan out into their own quadrants and never collide.
              const labelGap = 40;
              const lp = {
                x: CX + Math.cos(a) * (R_INNER + labelGap),
                y: CY + Math.sin(a) * (R_INNER + labelGap),
              };
              const isLeft = lp.x < CX - 4;
              const isRight = lp.x > CX + 4;
              const textAnchor = isLeft ? "end" : isRight ? "start" : "middle";

              const lines = splitLabel(m.name.toUpperCase());
              // Vertical baseline: one line → centred; two lines → first
              // line above, second line below the radial endpoint.
              const lineHeight = 12;
              const firstLineY = lines.length === 2 ? lp.y - 1 : lp.y + 4;

              return (
                <g
                  key={m.id}
                  data-testid={`orbit-module-${m.id}`}
                  style={{ opacity: 1, transition: "opacity 0.8s ease 0.4s" }}
                >
                  {/* radial line connecting centre to the module dot */}
                  <line x1={CX} y1={CY} x2={p.x} y2={p.y} stroke={T.warm} strokeOpacity="0.25" />

                  {/* the module dot */}
                  <circle cx={p.x} cy={p.y} r="22" fill={T.surface2} stroke={T.warm} strokeWidth="1" />

                  {/* short index marker inside the circle */}
                  <text
                    x={p.x}
                    y={p.y + 4}
                    textAnchor="middle"
                    fontFamily="IBM Plex Mono, monospace"
                    fontSize="10"
                    fill={T.warm}
                    letterSpacing="2"
                  >
                    M{i + 1}
                  </text>

                  {/* short connector hairline from circle edge to the label */}
                  <line
                    x1={p.x + Math.cos(a) * 22}
                    y1={p.y + Math.sin(a) * 22}
                    x2={lp.x - Math.cos(a) * 4}
                    y2={lp.y - Math.sin(a) * 4}
                    stroke={T.warm}
                    strokeOpacity="0.35"
                    strokeWidth="0.8"
                  />

                  {/* the full module name outside the circle, wrapped */}
                  <text
                    x={lp.x}
                    y={firstLineY}
                    textAnchor={textAnchor}
                    fontFamily="IBM Plex Mono, monospace"
                    fontSize="9.5"
                    fill={T.warm}
                    letterSpacing="1.2"
                  >
                    {lines.map((ln, k) => (
                      <tspan
                        key={k}
                        x={lp.x}
                        dy={k === 0 ? 0 : lineHeight}
                      >
                        {ln}
                      </tspan>
                    ))}
                  </text>
                </g>
              );
            })}

            {/* Erenown centre — fades when a brand takes the stage */}
            <g style={{ opacity: selected ? 0.22 : 1, transition: "opacity 0.8s ease" }} data-testid="orbit-center-erenown">
              <circle cx={CX} cy={CY} r="66" fill="url(#coreGlow)" />
              <circle cx={CX} cy={CY} r="42" fill={T.surface2} stroke={T.accent} strokeWidth="1.5" />
              <circle cx={CX} cy={CY} r="9" fill={T.accent} />
              <text x={CX} y={CY + 62} textAnchor="middle" fill={T.text} fontFamily="Fraunces, serif" fontSize="16">Erenown</text>
              <text x={CX} y={CY + 78} textAnchor="middle" fill={T.muted} fontFamily="IBM Plex Mono, monospace" fontSize="9" letterSpacing="2">CENTRAL STRUCTURE · <tspan fontFamily="Bricolage Grotesque, sans-serif" fontWeight="800" fill={T.warm} letterSpacing="0">E</tspan> · 00</text>
            </g>

            {/* Brands */}
            {BRANDS.map((b, i) => {
              const pos = brandPos(i);
              const isSel = selected === b.id;
              const isDim = selected && !isSel;
              const dx = isSel ? CX - pos.x : 0;
              const dy = isSel ? CY - pos.y : 0;
              return (
                <g key={b.id}
                  data-testid={`orbit-brand-${b.id}`}
                  onClick={() => pick(b.id)}
                  style={{
                    cursor: "pointer",
                    opacity: isDim ? 0.22 : 1,
                    transform: `translate(${dx}px, ${dy}px)`,
                    transition: "transform 1.2s cubic-bezier(.4,0,.2,1), opacity 0.8s ease",
                  }}>
                  {isSel && <circle cx={pos.x} cy={pos.y} r="80" fill="url(#warmGlow)" />}
                  <circle
                    cx={pos.x} cy={pos.y}
                    r={isSel ? 52 : 32}
                    fill={isSel ? T.surface3 : T.surface}
                    stroke={isSel ? T.warm : T.accent}
                    strokeWidth={isSel ? 1.5 : 1}
                    style={{ transition: "r 1.2s cubic-bezier(.4,0,.2,1), stroke 0.6s, fill 0.6s" }}
                  />
                  <text
                    x={pos.x}
                    y={pos.y + 5}
                    textAnchor="middle"
                    fontFamily="Fraunces, serif"
                    fontSize={isSel ? 18 : 12}
                    fill={isSel ? T.warm : T.text}
                    style={{ transition: "font-size 0.6s, fill 0.6s" }}
                  >
                    {b.name}
                  </text>
                  {!isSel && (
                    <text x={pos.x} y={pos.y + 50} textAnchor="middle" fontFamily="IBM Plex Mono, monospace" fontSize="8" fill={T.muted} letterSpacing="1.5">
                      {b.tagline.toUpperCase()}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Reset / hint */}
          <div className="mt-2 flex items-center justify-between font-plexmono text-[10px] tracking-[0.24em] uppercase" style={{ color: T.muted }}>
            <span>{selected ? "Centred brand · click again to return" : "Click a brand to centre it"}</span>
            {selected && (
              <button data-testid="orbit-reset" onClick={() => pick(selected)} className="transition-colors hover:text-white" style={{ color: T.accent }}>
                ← Return to Erenown
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Side panel */}
      <div className="col-span-12 lg:col-span-5" data-testid="orbit-side-panel">
        <div className="border" style={{ borderColor: T.border, background: T.surface }}>
          {/* Header */}
          <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: T.border }}>
            <span className="font-plexmono text-[10.5px] tracking-[0.24em] uppercase" style={{ color: T.accent }}>
              {selected ? `BRAND · ${selectedBrand.name.toUpperCase()}` : "ERENOWN · CENTRAL STRUCTURE"}
            </span>
            <span className="font-plexmono text-[10px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
              {selected ? "04 modules" : "05 brands"}
            </span>
          </div>

          {/* Body */}
          <div className="p-6 md:p-8">
            {!selected ? (
              <div data-testid="orbit-side-default">
                <h3 className="font-fraunces font-normal text-3xl md:text-4xl leading-[1.15]" style={{ fontVariationSettings: "'SOFT' 50" }}>
                  A structured orbit of brands around a central core.
                </h3>
                <p className="mt-5 font-intertight text-[15px] leading-[1.85]" style={{ color: T.textSoft }}>
                  Choose a brand to bring it into focus. Each one holds a distinct role within the wider
                  Erenown orbit.
                </p>
                <div className="mt-8 grid grid-cols-1 gap-2">
                  {BRANDS.map((b) => (
                    <button key={b.id} data-testid={`orbit-list-${b.id}`} onClick={() => pick(b.id)}
                      className="group text-left px-4 py-3 border transition-colors hover:border-white"
                      style={{ borderColor: T.border, color: T.text }}>
                      <div className="flex items-baseline justify-between gap-4">
                        <span className="font-fraunces text-lg">{b.name}</span>
                        <span className="font-plexmono text-[10px] tracking-[0.24em] uppercase" style={{ color: T.muted }}>{b.tagline}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div data-testid={`orbit-side-${selectedBrand.id}`}>
                <h3 className="font-fraunces font-normal text-4xl md:text-5xl leading-[1.1]" style={{ color: T.warm, fontVariationSettings: "'SOFT' 50" }}>
                  {selectedBrand.name}
                </h3>
                <div className="mt-2 font-plexmono text-[10.5px] tracking-[0.24em] uppercase" style={{ color: T.muted }}>
                  {selectedBrand.tagline}
                </div>
                <p className="mt-6 font-intertight text-[15px] leading-[1.85]" style={{ color: T.textSoft }}>
                  {selectedBrand.description}
                </p>

                <div className="mt-8 font-plexmono text-[10.5px] tracking-[0.24em] uppercase" style={{ color: T.muted }}>Modules</div>
                <ul className="mt-4 grid grid-cols-1 gap-px" style={{ background: T.border }}>
                  {selectedBrand.modules.map((m) => (
                    <li key={m.id} data-testid={`orbit-module-list-${m.id}`} className="p-4" style={{ background: T.surface2 }}>
                      <div className="flex items-baseline justify-between gap-4">
                        <span className="font-fraunces text-lg" style={{ color: T.text }}>{m.name}</span>
                        <span className="font-plexmono text-[10px] tracking-[0.24em] uppercase" style={{ color: T.warm }}>ON</span>
                      </div>
                      <p className="mt-2 font-intertight text-[13.5px] leading-[1.8]" style={{ color: T.mutedSoft }}>{m.body}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
